import { Component } from '@theme/component';
import { SlideshowSelectEvent } from '@theme/events';

/**
 * A small playback/accessibility layer around Horizon's native slideshow.
 * Native Slideshow owns scrolling, snapping, dragging and slide selection.
 * @extends {Component<{slideshow: import('./slideshow').Slideshow, playback: HTMLButtonElement, dots: HTMLButtonElement[]}>}
 */
class HoppipollaHeroCarousel extends Component {
  requiredRefs = ['slideshow', 'playback', 'dots'];
  #controller;
  #observer;
  #timer;
  #motion;
  #visible = false;
  #hovered = false;
  #userPaused = false;

  connectedCallback() {
    super.connectedCallback();
    this.#setup();
  }

  updatedCallback() {
    super.updatedCallback();
    this.#teardown();
    this.#setup();
  }

  disconnectedCallback() {
    this.#teardown();
    super.disconnectedCallback();
  }

  #setup() {
    this.#controller = new AbortController();
    const { signal } = this.#controller;
    this.#motion = window.matchMedia('(prefers-reduced-motion: reduce)');
    this.#visible = false;
    this.#hovered = this.matches(':hover') && window.matchMedia('(hover: hover)').matches;
    this.#userPaused = this.contains(document.activeElement);
    this.addEventListener('mouseenter', () => { this.#hovered = true; this.#schedule(); }, { signal });
    this.addEventListener('mouseleave', () => { this.#hovered = false; this.#schedule(); }, { signal });
    this.addEventListener('focusin', this.#pauseForInteraction, { signal });
    this.addEventListener('keydown', this.#handleKeydown, { signal });
    this.addEventListener('pointerdown', this.#pauseForInteraction, { signal });
    this.addEventListener(SlideshowSelectEvent.eventName, this.#handleSelection, { signal });
    document.addEventListener('visibilitychange', () => this.#schedule(), { signal });
    this.#motion.addEventListener('change', () => this.#schedule(), { signal });
    this.#observer = new IntersectionObserver(([entry]) => {
      this.#visible = entry.isIntersecting;
      this.#schedule();
    });
    this.#observer.observe(this);
    customElements.whenDefined('slideshow-component').then(() => {
      if (signal.aborted) return;
      this.setAttribute('data-ready', '');
      this.#setActive(this.refs.slideshow.current || 0);
      this.#schedule();
    });
  }

  #teardown() {
    clearTimeout(this.#timer);
    this.#controller?.abort();
    this.#observer?.disconnect();
    this.removeAttribute('data-ready');
  }

  #pauseForInteraction = (event) => {
    if (this.refs.playback.contains(event.target)) return;
    this.#userPaused = true;
    this.#schedule();
  };

  #schedule() {
    clearTimeout(this.#timer);
    if (this.#controller.signal.aborted) return;
    const paused = this.#userPaused || this.#motion.matches;
    const { playback, slideshow } = this.refs;
    playback.setAttribute('aria-label', paused ? playback.dataset.play : playback.dataset.pause);
    playback.dataset.paused = String(paused);
    playback.disabled = this.#motion.matches;
    slideshow.setAttribute('aria-live', paused ? 'polite' : 'off');
    if (paused || this.#hovered || !this.#visible || document.hidden) return;
    this.#timer = setTimeout(() => {
      slideshow.next();
      this.#schedule();
    }, 6000);
  }

  /** Explicitly resume after keyboard or pointer interaction. */
  toggleAutoplay() {
    if (this.#motion.matches) return;
    this.#userPaused = !this.#userPaused;
    this.#schedule();
  }

  /** @param {Event} event */
  next(event) { this.refs.slideshow.next(event); }

  /** @param {Event} event */
  previous(event) { this.refs.slideshow.previous(event); }

  /** @param {string | number} index @param {Event} event */
  select(index, event) { this.refs.slideshow.select(Number(index), event); }

  /** @param {KeyboardEvent} event */
  #handleKeydown = (event) => {
    if (this.refs.playback.contains(event.target) && ['Enter', ' '].includes(event.key)) return;
    this.#userPaused = true;
    this.#schedule();
    const { slideshow } = this.refs;
    switch (event.key) {
      case 'ArrowRight': slideshow.next(event); break;
      case 'ArrowLeft': slideshow.previous(event); break;
      case 'Home': event.preventDefault(); slideshow.select(0, event); break;
      case 'End': event.preventDefault(); slideshow.select(slideshow.slides.length - 1, event); break;
    }
  };

  /** @param {SlideshowSelectEvent} event */
  #handleSelection = (event) => {
    if (event.target !== this.refs.slideshow) return;
    this.#setActive(event.detail.index);
    this.#schedule();
  };

  /** Keep hidden links out of the keyboard sequence; dots are ordinary buttons. @param {number} index */
  #setActive(index) {
    for (const [i, slide] of this.refs.slideshow.slides.entries()) {
      slide.inert = i !== index;
    }
    for (const [i, dot] of this.refs.dots.entries()) {
      if (i === index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    }
  }
}

if (!customElements.get('hoppipolla-hero-carousel')) {
  customElements.define('hoppipolla-hero-carousel', HoppipollaHeroCarousel);
}
